import { Card, CardContent } from "@/components/ui/card";
import { Button } from "./ui/button";
import { ArrowRight, TrendingDown, TrendingUp, Zap } from "lucide-react";
import { Link } from "react-router-dom";

const CaseStudyCards = () => {
  const caseStudies = [
    {
      icon: TrendingDown,
      industry: "Cleaning Services",
      title: "Regional Cleaning Company",
      metric: "40% Labor Cost Reduction",
      description: "Deployed autonomous cleaning robots across multiple commercial properties, cutting labor costs while improving service quality and consistency.",
      results: [
        "$4,200/month savings",
        "24/7 automated cleaning",
        "Zero customer complaints"
      ],
      iconColor: "text-primary"
    },
    {
      icon: TrendingUp,
      industry: "Hospitality",
      title: "Boutique Hotel Chain",
      metric: "30% Faster Guest Service",
      description: "Integrated delivery robots and AI voice systems to handle amenity requests, room service, and guest communications seamlessly.",
      results: [
        "Guest satisfaction: 4.2 → 4.7★",
        "Staff productivity +35%",
        "8-month ROI achieved"
      ],
      iconColor: "text-accent"
    },
    {
      icon: Zap,
      industry: "Retail & Convenience",
      title: "Multi-Location Retail Chain",
      metric: "50% Faster Checkout Times",
      description: "AI-powered inventory management and automated checkout systems reduced wait times and improved customer experience across 12 locations.",
      results: [
        "Checkout time: 4min → 2min",
        "Customer retention +28%",
        "Labor savings: $7,500/month"
      ],
      iconColor: "text-primary"
    }
  ];

  return (
    <section className="py-20">
      <div className="text-center mb-12">
        <h2 className="font-orbitron font-bold text-4xl md:text-5xl mb-4">
          Real Results, <span className="text-primary">Real ROI</span>
        </h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          See how businesses are transforming operations with Neil AI automation
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-12">
        {caseStudies.map((study, index) => {
          const Icon = study.icon;
          return (
            <Card 
              key={index} 
              className="glass-effect border-[hsl(var(--line))] hover:border-primary/40 transition-all duration-300 group overflow-hidden"
            >
              <CardContent className="p-6">
                {/* Industry badge */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold text-primary uppercase tracking-wider">
                    {study.industry}
                  </span>
                  <div className={`w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center ${study.iconColor}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="font-orbitron font-bold text-xl mb-2 text-foreground">
                  {study.title}
                </h3>

                {/* Main metric */}
                <div className="text-2xl font-bold text-primary mb-4">
                  {study.metric}
                </div>

                {/* Description */}
                <p className="text-sm text-muted-foreground mb-4">
                  {study.description}
                </p>

                {/* Results list */}
                <ul className="space-y-2 mb-6">
                  {study.results.map((result, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-primary font-bold mt-0.5">✓</span>
                      <span className="text-muted-foreground">{result}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* CTA to full case studies */}
      <div className="text-center">
        <Button 
          asChild
          variant="outline"
          size="lg"
          className="border-2 border-primary/30 hover:bg-primary/10 hover:border-primary/60 font-bold rounded-xl px-8 py-6 group"
        >
          <Link to="/case-studies">
            Read Full Success Stories
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </div>
    </section>
  );
};

export default CaseStudyCards;
